import React, { useState, useEffect, useRef, useCallback, useMemo, lazy, Suspense } from 'react';
import ErrorBoundary from './lib/error-boundary';
import { initScrollReveal } from './lib/scroll-reveal';
import { Icon, Spinner, ErrorBanner } from './components/primitives';
import { apiFetch, trackPageview, trackEvent } from './lib/utils';
import LandingView from './views/LandingView';
const InventoryView         = lazy(() => import('./views/InventoryView'));
const EquipmentDetailView   = lazy(() => import('./views/EquipmentDetailView'));
const DealersView           = lazy(() => import('./views/DealersView'));
const CompareView           = lazy(() => import('./views/CompareView'));
const LoginView             = lazy(() => import('./views/LoginView'));
const EnrollView            = lazy(() => import('./views/EnrollView'));
const PlansView             = lazy(() => import('./views/PlansView'));
const DashboardView         = lazy(() => import('./views/DashboardView'));
const PrivacyView           = lazy(() => import('./views/PrivacyView'));
const TermsView             = lazy(() => import('./views/TermsView'));
const ContactView           = lazy(() => import('./views/ContactView'));

// Preload helper for EquipmentDetailView. InventoryView fires this on mount
// so the detail chunk is cached before the user clicks an equipment card —
// keeps the FLIP transition snappy without a network round-trip wait.
const preloadEquipmentDetail = () => import('./views/EquipmentDetailView');

// Run scroll-reveal bootstrap once when the module loads
initScrollReveal();

// ─── URL ↔ Tab routing helpers (verbatim from old index.html 7789–7807) ───
const TAB_PATHS = {
  '/inventory': 'inventory',
  '/compare':   'compare',
  '/dealers':   'dealers',
  '/plans':     'plans',
  '/dashboard': 'dashboard',
  '/login':     'login',
  '/enroll':    'enroll',
  '/privacy':   'privacy',
  '/terms':     'terms',
  '/contact':   'contact',
};
function pathToTab(path) {
  return TAB_PATHS[path] || 'home';
}
function tabToPath(tab) {
  return tab === 'home' ? '/' : '/' + tab;
}

function LoadingFallback() {
  return (
    <div style={{
      minHeight: '60vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <Spinner />
    </div>
  );
}

// ─── App Root (verbatim from old index.html 7810–8283) ───
function App() {
  const [tab, setTab] = useState(() => pathToTab(window.location.pathname));
  const [dealers, setDealers] = useState([]);
  const [compareList, setCompareList] = useState([]);
  const [session, setSession] = useState(null);
  const [currentDealer, setCurrentDealer] = useState(null);
  const [selectedEquipmentId, setSelectedEquipmentId] = useState(null);
  const [inventorySearch, setInventorySearch] = useState(() => {
    if (pathToTab(window.location.pathname) === 'inventory') {
      return new URLSearchParams(window.location.search).get('search') || '';
    }
    return '';
  });
  const [inventoryType, setInventoryType] = useState(() => {
    if (pathToTab(window.location.pathname) === 'inventory') {
      return new URLSearchParams(window.location.search).get('type') || '';
    }
    return '';
  });
  const [adConfig, setAdConfig] = useState({});
  const [enrollPlan, setEnrollPlan] = useState('free');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState(null); // null | 'success' | 'error'

  // ─── Load compare list from shareable URL ───
  useEffect(() => {
    if (tab !== 'compare') return;
    const idsParam = new URLSearchParams(window.location.search).get('ids');
    if (!idsParam) return;
    const ids = idsParam.split(',').filter(Boolean).slice(0, 4);
    if (ids.length === 0 || compareList.length > 0) return;
    apiFetch(`/equipment?ids=${ids.join(',')}`)
      .then(res => { if (res.data && res.data.length) setCompareList(res.data); })
      .catch(() => {});
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Sync compare list → URL ───
  useEffect(() => {
    if (tab !== 'compare') return;
    const ids = compareList.map(i => i.id).join(',');
    const url = ids ? `/compare?ids=${ids}` : '/compare';
    if (window.location.pathname + window.location.search !== url) {
      history.replaceState(null, '', url);
    }
  }, [tab, compareList]);

  function handleNewsletterSubmit() {
    const email = newsletterEmail.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setNewsletterStatus('error');
      return;
    }
    fetch('/api/newsletter/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    }).then(() => {
      setNewsletterStatus('success');
      setNewsletterEmail('');
    }).catch(() => setNewsletterStatus('error'));
  }

  // Restore session from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('ls_token');
    if (saved) {
      apiFetch('/auth/me', {}, saved)
        .then(d => {
          // Reconstruct a minimal session object
          setSession({ access_token: saved });
          setCurrentDealer(d.data.dealer);
        })
        .catch(() => {
          localStorage.removeItem('ls_token');
        });
    }
  }, []);

  // Pre-fetch dealers for equipment forms + inventory dealer labels
  useEffect(() => {
    apiFetch('/dealers')
      .then(d => setDealers(d.data || []))
      .catch(() => {});
  }, []);

  // Fetch ad config and optionally load AdSense + GA4 scripts
  useEffect(() => {
    apiFetch('/config').then(d => {
      setAdConfig(d.data || {});
      if (d.data && d.data.adsensePubId && !document.getElementById('adsense-script')) {
        const s = document.createElement('script');
        s.id = 'adsense-script';
        s.async = true;
        s.crossOrigin = 'anonymous';
        s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${d.data.adsensePubId}`;
        document.head.appendChild(s);
      }
      if (d.data && d.data.gaMeasurementId && !document.getElementById('ga-script')) {
        const id = d.data.gaMeasurementId;
        const s = document.createElement('script');
        s.id = 'ga-script';
        s.async = true;
        s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
        document.head.appendChild(s);
        window.dataLayer = window.dataLayer || [];
        window.gtag = function() { window.dataLayer.push(arguments); };
        window.gtag('js', new Date());
        // SPA: disable auto-pageview — we'll fire them manually on tab change
        window.gtag('config', id, { send_page_view: false });
        window.__GA_ID__ = id;
        // Fire pageview for initial load once gtag is ready
        s.addEventListener('load', () => {
          trackPageview(window.location.pathname + window.location.search, pathToTab(window.location.pathname));
        });
      }
    }).catch(() => {});
  }, []);

  // Handle browser back/forward navigation
  useEffect(() => {
    function handlePop() {
      const newTab = pathToTab(window.location.pathname);
      setTab(newTab);
      if (newTab === 'inventory') {
        const params = new URLSearchParams(window.location.search);
        setInventorySearch(params.get('search') || '');
        setInventoryType(params.get('type') || '');
      } else {
        setInventorySearch('');
        setInventoryType('');
      }
    }
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, []);

  function handleLoginSuccess(newSession, dealer) {
    setSession(newSession);
    setCurrentDealer(dealer);
    localStorage.setItem('ls_token', newSession.access_token);
  }

  function navigateTo(newTab, searchTerm, plan) {
    setTab(newTab);
    setSelectedEquipmentId(null);
    setMobileMenuOpen(false);
    if (newTab === 'enroll' && plan) setEnrollPlan(plan);
    let path = tabToPath(newTab);
    if (newTab === 'inventory' && searchTerm) {
      setInventorySearch(searchTerm);
      path = tabToPath(newTab) + '?search=' + encodeURIComponent(searchTerm);
      window.history.pushState(null, '', path);
    } else {
      if (newTab !== 'inventory') setInventorySearch('');
      window.history.pushState(null, '', path);
    }
    trackPageview(path, newTab);
  }

  function handleViewEquipment(id) {
    setTab('inventory');
    setSelectedEquipmentId(id);
    setMobileMenuOpen(false);
    window.history.pushState(null, '', '/inventory');
  }

  function handleLogout() {
    const token = session && session.access_token;
    if (token) {
      apiFetch('/auth/logout', { method: 'POST' }, token).catch(() => {});
    }
    setSession(null);
    setCurrentDealer(null);
    localStorage.removeItem('ls_token');
    navigateTo('home');
  }

  function handleToggleCompare(item) {
    setCompareList(prev => {
      const inList = prev.some(c => c.id === item.id);
      if (inList) {
        trackEvent('compare_removed', { equipment_id: item.id });
        return prev.filter(c => c.id !== item.id);
      }
      if (prev.length >= 4) {
        alert('You can compare up to 4 items at a time. Remove one first.');
        return prev;
      }
      trackEvent('compare_added', {
        equipment_id: item.id,
        equipment_name: item.name,
        compare_count: prev.length + 1,
      });
      return [...prev, item];
    });
  }

  function handleRemoveCompare(item) {
    setCompareList(prev => prev.filter(c => c.id !== item.id));
  }

  function handleClearCompare() {
    setCompareList([]);
  }

  const loggedIn = !!session;

  const PUBLIC_TABS = [
    { id: 'home',      label: 'Home',      icon: 'home' },
    { id: 'inventory', label: 'Inventory', icon: 'inventory_2' },
    { id: 'compare',   label: 'Compare',   icon: 'compare_arrows' },
    { id: 'dealers',   label: 'Dealers',   icon: 'storefront' },
    { id: 'plans',     label: 'Plans',     icon: 'payments' },
  ];

  const TABS = loggedIn
    ? [...PUBLIC_TABS, { id: 'dashboard', label: 'Dashboard', icon: 'dashboard' }]
    : PUBLIC_TABS;

  return (
    <>
      <a href="#main-content" className="skip-to-content">Skip to content</a>
      {/* ── Nav ── */}
      <nav className="nav" role="navigation" aria-label="Main navigation">
        <button
          className="nav-logo"
          style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          onClick={() => navigateTo('home')}
          aria-label="LiftMarket home"
        >
          LIFT<span style={{ color: '#fff' }}>MARKET</span>
        </button>

        <div className="nav-tabs" role="tablist">
          {TABS.map(t => (
            <button
              key={t.id}
              role="tab"
              aria-selected={tab === t.id}
              className={`nav-tab${tab === t.id ? ' active' : ''}`}
              onClick={() => navigateTo(t.id)}
            >
              <Icon name={t.icon} style={{ fontSize: 15 }} />
              {t.label}
              {t.id === 'compare' && compareList.length > 0 && (
                <span style={{
                  background: 'var(--primary)',
                  color: 'var(--on-primary)',
                  fontSize: 9,
                  fontWeight: 700,
                  padding: '1px 5px',
                  borderRadius: 'var(--radius-pill)',
                  marginLeft: 2,
                }}>
                  {compareList.length}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="nav-actions">
          {loggedIn ? (
            <>
              <button className="btn btn-ghost btn-sm" onClick={() => navigateTo('dashboard')}>
                <Icon name="dashboard" style={{ fontSize: 13 }} />
                {currentDealer ? currentDealer.company : 'Dashboard'}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={handleLogout}>
                <Icon name="logout" style={{ fontSize: 13 }} />
                Logout
              </button>
            </>
          ) : (
            <>
              <button className="btn btn-ghost btn-sm" onClick={() => navigateTo('login')}>
                <Icon name="login" style={{ fontSize: 13 }} />
                Dealer Login
              </button>
              <button className="btn btn-primary btn-sm" onClick={() => navigateTo('enroll')}>
                <Icon name="person_add" style={{ fontSize: 13 }} />
                Enroll Now
              </button>
            </>
          )}
        </div>

        <button
          className="nav-hamburger"
          onClick={() => setMobileMenuOpen(prev => !prev)}
          aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileMenuOpen}
        >
          <Icon name={mobileMenuOpen ? 'close' : 'menu'} style={{ fontSize: 22 }} />
        </button>

        {mobileMenuOpen && (
          <div className="nav-mobile-menu" role="dialog" aria-label="Mobile navigation">
            {TABS.map(t => (
              <button
                key={t.id}
                className={tab === t.id ? 'active' : ''}
                onClick={() => navigateTo(t.id)}
              >
                <Icon name={t.icon} style={{ fontSize: 16 }} />
                {t.label}
                {t.id === 'compare' && compareList.length > 0 && (
                  <span style={{
                    background: 'var(--primary)',
                    color: 'var(--on-primary)',
                    fontSize: 9,
                    fontWeight: 700,
                    padding: '1px 5px',
                    borderRadius: 'var(--radius-pill)',
                    marginLeft: 4,
                  }}>
                    {compareList.length}
                  </span>
                )}
              </button>
            ))}
            <div className="mobile-divider" />
            {loggedIn ? (
              <>
                <button onClick={() => navigateTo('dashboard')}>
                  <Icon name="dashboard" style={{ fontSize: 16 }} />
                  {currentDealer ? currentDealer.company : 'Dashboard'}
                </button>
                <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }}>
                  <Icon name="logout" style={{ fontSize: 16 }} />
                  Logout
                </button>
              </>
            ) : (
              <>
                <button onClick={() => navigateTo('login')}>
                  <Icon name="login" style={{ fontSize: 16 }} />
                  Dealer Login
                </button>
                <button className="mobile-action-primary" onClick={() => navigateTo('enroll')}>
                  <Icon name="person_add" style={{ fontSize: 16 }} />
                  Enroll Now
                </button>
              </>
            )}
          </div>
        )}
      </nav>

      {/* ── Main ── */}
      <main id="main-content" role="main" style={{ overflowX: 'hidden' }}>
        <Suspense fallback={<LoadingFallback />}>
        {tab === 'home' && <LandingView onNavigate={navigateTo} onViewEquipment={handleViewEquipment} adConfig={adConfig} />}
        {tab === 'login' && (
          <div className="main">
            <LoginView onNavigate={navigateTo} onLoginSuccess={handleLoginSuccess} />
          </div>
        )}
        {tab === 'enroll' && (
          <div className="main">
            <EnrollView onNavigate={navigateTo} onLoginSuccess={handleLoginSuccess} initialTier={enrollPlan} />
          </div>
        )}
        {tab === 'plans' && (
          <div className="main">
            <PlansView onNavigate={navigateTo} session={session} currentDealer={currentDealer} />
          </div>
        )}
        {tab === 'inventory' && (
          <>
            {selectedEquipmentId ? (
              <div className="main">
                <EquipmentDetailView
                  equipmentId={selectedEquipmentId}
                  onBack={() => setSelectedEquipmentId(null)}
                  onViewSimilar={(id) => setSelectedEquipmentId(id)}
                  compareList={compareList}
                  onToggleCompare={handleToggleCompare}
                  adConfig={adConfig}
                />
              </div>
            ) : (
              <InventoryView
                dealers={dealers}
                compareList={compareList}
                onToggleCompare={handleToggleCompare}
                onViewEquipment={setSelectedEquipmentId}
                initialSearch={inventorySearch}
                initialType={inventoryType}
                onSearchChange={setInventorySearch}
                onPreloadDetail={preloadEquipmentDetail}
              />
            )}
          </>
        )}
        {tab === 'dealers' && <div className="main"><DealersView /></div>}
        {tab === 'compare' && (
          <div className="main">
            <CompareView
              compareList={compareList}
              onRemove={handleRemoveCompare}
              onClearAll={handleClearCompare}
            />
          </div>
        )}
        {tab === 'dashboard' && loggedIn && (
          <DashboardView
            session={session}
            currentDealer={currentDealer}
            onNavigate={navigateTo}
          />
        )}
        {tab === 'dashboard' && !loggedIn && (
          <div className="main" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 24, textAlign: 'center' }}>
            <span className="material-symbols-outlined" style={{ fontSize: 48, color: 'var(--primary)' }}>lock</span>
            <h2 style={{ fontFamily: 'var(--font-head)', fontWeight: 700, fontSize: 24, color: 'var(--on-surface)', margin: 0 }}>Dealer Login Required</h2>
            <p style={{ color: '#a09e9d', fontFamily: 'var(--font-body)', fontSize: 14, margin: 0 }}>Please sign in to access your dealer dashboard.</p>
            <button className="btn btn-primary" onClick={() => navigateTo('login')}>Sign In</button>
          </div>
        )}
        {tab === 'privacy' && <div className="main"><PrivacyView /></div>}
        {tab === 'terms' && <div className="main"><TermsView /></div>}
        {tab === 'contact' && <div className="main"><ContactView onNavigate={navigateTo} /></div>}
        </Suspense>
      </main>

      {/* ── Footer ── */}
      <footer style={{ background: 'var(--surface-lowest)', borderTop: '1px solid rgba(85,67,54,0.1)', marginTop: 'auto' }}>
        <div className="site-footer-grid">
          {/* Brand */}
          <div>
            <button
              onClick={() => navigateTo('home')}
              style={{ fontFamily: 'var(--font-head)', fontWeight: 900, fontSize: 20, color: 'var(--on-surface)', textTransform: 'uppercase', letterSpacing: '-0.03em', background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginBottom: 16, display: 'block' }}
            >LIFTMARKET Industrial</button>
            <p style={{ color: '#a09e9d', fontFamily: 'var(--font-body)', fontSize: 12, lineHeight: 1.7, maxWidth: 280, marginBottom: 32 }}>
              The world's most sophisticated marketplace for industrial material handling equipment. Precision at every lift.
            </p>
            <div style={{ display: 'flex', gap: 16 }}>
              {['language', 'settings_accessibility', 'verified_user'].map(icon => (
                <Icon key={icon} name={icon} style={{ color: 'var(--outline)', cursor: 'pointer', fontSize: 20, transition: 'color 150ms' }} />
              ))}
            </div>
          </div>
          {/* Navigation */}
          <div>
            <h4 style={{ fontFamily: 'var(--font-head)', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.2em', color: 'var(--on-surface)', marginBottom: 24 }}>Quick Navigation</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {[['Inventory', 'inventory'], ['Dealers', 'dealers'], ['Plans', 'plans'], ['Contact', 'contact']].map(([label, page]) => (
                <a key={label} href="#" onClick={e => { e.preventDefault(); navigateTo(page); }} style={{ color: '#a09e9d', fontSize: 12, fontFamily: 'var(--font-body)', textDecoration: 'none', textTransform: 'uppercase', letterSpacing: '0.2em', transition: 'color 150ms' }}>{label}</a>
              ))}
            </div>
          </div>
          {/* Legal */}
          <div>
            <h4 style={{ fontFamily: 'var(--font-head)', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.2em', color: 'var(--on-surface)', marginBottom: 24 }}>Legal & Compliance</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {[['Terms of Service', 'terms'], ['Privacy Policy', 'privacy'], ['Dealer Agreement', 'enroll']].map(([label, page]) => (
                <a key={label} href="#" onClick={e => { e.preventDefault(); navigateTo(page); }} style={{ color: '#a09e9d', fontSize: 12, fontFamily: 'var(--font-body)', textDecoration: 'none', textTransform: 'uppercase', letterSpacing: '0.2em', transition: 'color 150ms' }}>{label}</a>
              ))}
            </div>
          </div>
          {/* Newsletter */}
          <div>
            <h4 style={{ fontFamily: 'var(--font-head)', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.2em', color: 'var(--on-surface)', marginBottom: 24 }}>Newsletter</h4>
            <p style={{ color: '#a09e9d', fontSize: 12, fontFamily: 'var(--font-body)', marginBottom: 16 }}>Subscribe for industrial market reports and inventory alerts.</p>
            {newsletterStatus === 'success' ? (
              <p style={{ color: 'var(--primary)', fontSize: 12, fontFamily: 'var(--font-body)' }}>You're subscribed! We'll be in touch.</p>
            ) : (
              <>
                <div style={{ display: 'flex' }}>
                  <input
                    type="email"
                    placeholder="Email Address"
                    value={newsletterEmail}
                    onChange={e => { setNewsletterEmail(e.target.value); setNewsletterStatus(null); }}
                    onKeyDown={e => e.key === 'Enter' && handleNewsletterSubmit()}
                    style={{ flex: 1, background: 'var(--surface-container)', border: newsletterStatus === 'error' ? '1px solid #e05c5c' : 'none', color: 'var(--on-surface)', fontSize: 12, padding: '10px 16px', outline: 'none', fontFamily: 'var(--font-body)' }}
                  />
                  <button onClick={handleNewsletterSubmit} style={{ background: 'var(--primary-container)', border: 'none', padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon name="send" style={{ color: 'var(--on-primary-container)', fontSize: 16 }} />
                  </button>
                </div>
                {newsletterStatus === 'error' && (
                  <p style={{ color: '#e05c5c', fontSize: 11, fontFamily: 'var(--font-body)', marginTop: 4 }}>Please enter a valid email address.</p>
                )}
              </>
            )}
          </div>
        </div>
        <div className="site-footer-bottom">
          <span style={{ fontSize: 10, color: 'var(--outline)', textTransform: 'uppercase', letterSpacing: '0.2em', fontFamily: 'var(--font-body)' }}>© 2026 LiftMarket Industrial. Engineered for Precision.</span>
          <div className="footer-meta-desktop">
            <span style={{ fontSize: 10, color: 'var(--outline)', fontFamily: 'var(--font-body)' }}>US / ENG</span>
            <span style={{ fontSize: 10, color: 'var(--outline)', fontFamily: 'var(--font-body)' }}>SYST. STATUS: NOMINAL</span>
          </div>
        </div>
      </footer>
    </>
  );
}

export default function Root() {
  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
}
